import { useEffect, useRef, useState } from 'react'
import { HubbingIcon } from './icons'
import { normalizeCompanyUid, registryLinkEnabled } from './companyZefixLink'
import RegistryLink from './RegistryLink'
import { readOwnRegistryDocument } from './ownRegistryDocument'
import './OwnRegistryDocumentButton.css'

const COPY = {
  fr:{label:'Ma publication officielle FOSC (PDF)',hint:'Dernière publication au registre du commerce',busy:'Recherche de la publication…',error:'La publication PDF est temporairement indisponible. Vous pouvez consulter votre fiche Zefix.',session:'Votre session a changé. Reconnectez-vous avant de consulter votre publication.',timeout:'La recherche prend trop de temps. Réessayez ou consultez votre fiche Zefix.',fallback:'Consulter ma fiche Zefix'},
  de:{label:'Meine amtliche SHAB-Publikation (PDF)',hint:'Letzte Publikation im Handelsregister',busy:'Publikation wird gesucht…',error:'Das PDF ist vorübergehend nicht verfügbar. Sie können Ihren Zefix-Eintrag ansehen.',session:'Ihre Sitzung hat sich geändert. Bitte melden Sie sich erneut an.',timeout:'Die Suche dauert zu lange. Versuchen Sie es erneut oder öffnen Sie Zefix.',fallback:'Meinen Zefix-Eintrag ansehen'},
  it:{label:'La mia pubblicazione ufficiale FUSC (PDF)',hint:'Ultima pubblicazione nel registro di commercio',busy:'Ricerca della pubblicazione…',error:'Il PDF è temporaneamente non disponibile. Può consultare la scheda Zefix.',session:'La sessione è cambiata. Acceda nuovamente.',timeout:'La ricerca richiede troppo tempo. Riprovi oppure consulti Zefix.',fallback:'Consulta la mia scheda Zefix'},
  en:{label:'My official SOGC publication (PDF)',hint:'Latest publication in the commercial register',busy:'Finding the publication…',error:'The PDF is temporarily unavailable. You can view your Zefix record.',session:'Your session has changed. Sign in again to view your publication.',timeout:'The search is taking too long. Try again or view your Zefix record.',fallback:'View my Zefix record'},
}

export default function OwnRegistryDocumentButton({userId,company,isActive = true,lang = 'fr',onOpenDocument}) {
  const uid = normalizeCompanyUid(company?.zefix_uid)
  if (!userId || !uid || !registryLinkEnabled()) return null
  return <OwnRegistryDocumentContent key={`${userId}:${uid}`} userId={userId} company={company} uid={uid} isActive={isActive} lang={lang} onOpenDocument={onOpenDocument}/>
}
function OwnRegistryDocumentContent({userId,company,uid,isActive,lang,onOpenDocument}) {
  const [busy,setBusy] = useState(false),[errorCode,setErrorCode] = useState('')
  const pending = useRef(null),scope = useRef('')
  const identity = `${userId}:${uid}`
  scope.current = `${identity}:${isActive}`
  const text = COPY[lang] || COPY.fr
  const errorText = errorCode === 'auth_changed' ? text.session : errorCode === 'timeout' ? text.timeout : text.error
  useEffect(() => {
    setBusy(false)
    return () => {
      pending.current?.abort()
      pending.current = null
    }
  },[identity,isActive])
  const openDocument = async event => {
    const opener = event.currentTarget
    if (!isActive || pending.current || typeof onOpenDocument !== 'function') return
    const controller = new AbortController(),requestedScope = scope.current
    pending.current = controller
    setBusy(true);setErrorCode('')
    try {
      const document = await readOwnRegistryDocument({expectedUserId:userId,signal:controller.signal})
      if (!controller.signal.aborted && scope.current === requestedScope && pending.current === controller) onOpenDocument({...document,title:company?.name || text.label,opener})
    } catch (failure) {
      if (!controller.signal.aborted && scope.current === requestedScope && pending.current === controller) {
        setErrorCode(failure?.code === 'auth_changed' ? 'auth_changed' : failure?.code === 'timeout' ? 'timeout' : 'unavailable')
      }
    } finally {
      if (!controller.signal.aborted && pending.current === controller) {pending.current = null;setBusy(false)}
    }
  }
  return <div className="hb-registry-own-document">
    <button type="button" className="hb-registry-own-document-button" onClick={openDocument} disabled={busy || !isActive || typeof onOpenDocument !== 'function'} aria-busy={busy}>
      <HubbingIcon name="building" size={15} color="currentColor"/><span>{busy ? text.busy : text.label}</span>
    </button>
    <p className="hb-registry-own-document-hint">{text.hint}</p>
    {errorCode && <><p className="hb-registry-own-document-error" role="alert">{errorText}</p><RegistryLink company={company} authenticated={Boolean(userId)} requirePaidPlan={false} lang={lang} label={text.fallback}/></>}
  </div>
}
